import { createAsyncThunk } from '@reduxjs/toolkit';

import {
	repaginate,
	generateGlossary,
	updateResource,
	getPageData,
	getAllPagesData,
	getResourceCurrentHead,
	retrieveSubtree,
	retrieveSubtreeDiff,
	getCommits,
	getGeneratedQuestions,
	getGeneratedConcepts
} from '@editor2/services/apiService';
import {
	type RetrieveSubtreePayload,
	type RetrieveSubtreeDiffPayload,
	type GetPageDataPayload,
	type GetAllPagesDataPayload,
	type GetCommitsPayload,
	type UpdateResourcePayload,
	type QuestionGenerationContext,
	type ConceptGenerationContext
} from '@editor2/types/apiPayloads';
import {
	updateCourseConfigAndHeadCommit,
	setShowConflictsModal,
	storeQuestionGenerationPayload,
	storeQuestionGenerationResponse,
	setEocConceptsGenerating,
	appendEocKeyConcepts
} from '@editor2/reducers/editor/editorSlice';
import { store } from '@editor2/store';
import { showToast } from '@editor2/reducers/toastSlice';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import { resolvePromise } from '@editor2/services/promiseService';
import { createChangesPayload } from '@editor2/services/unsavedChangesService';
import { showErrorToast } from '@editor2/services/toastService';
import { validateChangesPayload } from '@editor2/services/validateChangesPayloadService';
import { validateYAMLConfig } from '@editor2/services/yamlValidationService';
import { type MCChoicePayload, type MCQuestion } from '@editor2/types/pageElements';
import { getNewElement } from '@editor2/services/elementService';

export const fetchCourse = createAsyncThunk('fetchCourse', async (courseId: number) => {
	const response = await getResourceCurrentHead(courseId);
	return response.data;
});

export const fetchCourseResourceSubtree = createAsyncThunk(
	'fetchCourseResourceSubtree',
	async (payload: RetrieveSubtreePayload) => {
		const response = await retrieveSubtree(payload);
		return response.data;
	}
);

export const fetchNoConflictingResourceDiff = createAsyncThunk(
	'fetchNoConflictingResourceDiff',
	async (payload: RetrieveSubtreeDiffPayload) => {
		const response = await retrieveSubtreeDiff(payload);
		return response.data;
	}
);

export const fetchPossiblyConflictingResourceDiff = createAsyncThunk(
	'fetchPossiblyConflictingResourceDiff',
	async (payload: RetrieveSubtreeDiffPayload) => {
		const response = await retrieveSubtreeDiff(payload);
		return response.data;
	}
);

export const fetchResourceDiffOnPushNotification = createAsyncThunk(
	'fetchResourceDiffOnPushNotification',
	async (payload: RetrieveSubtreeDiffPayload) => {
		// blocks UI only while update is being applied
		const response = await retrieveSubtreeDiff(payload);
		return response.data;
	}
);

export const fetchPageWithElementsData = createAsyncThunk(
	'fetchPageWithElements',
	async (payload: GetPageDataPayload) => {
		const response = await getPageData(payload);
		return response.data;
	}
);

export const fetchAllPagesWithElementsData = createAsyncThunk(
	'fetchAllPagesWithElementsData',
	async (payload: GetAllPagesDataPayload) => {
		const response: any = await getAllPagesData(payload);
		return { pages: response.map((page) => page.data) };
	}
);

export const fetchCommits = createAsyncThunk('fetchCommits', async (payload: GetCommitsPayload) => {
	const response = await getCommits(payload);
	return response.data;
});

export const saveResourceChanges = createAsyncThunk(
	'saveResourceChanges',
	async (payload: UpdateResourcePayload) => {
		const { courseId, resourceCommit } = payload;

		const updateResourcePromise = updateResource(payload);

		const [data, error] = await resolvePromise(updateResourcePromise);

		if (data) {
			// fast-forward update

			const { data: response } = data;

			store.dispatch(
				fetchNoConflictingResourceDiff({
					baseResourceId: resourceCommit.thing_id,
					headResourceId: response.headCommit.commit.thing_id,
					courseId
				})
			);
			store.dispatch(fetchCourse(courseId));
			store.dispatch(showToast({ severity: ToastSeverity.Success, message: 'Page saved!' }));
		} else if (error && error.response?.status === 409) {
			// not fast-forward update

			const response = await getResourceCurrentHead(courseId);
			const fetchResourceDiffPromise = store.dispatch(
				fetchPossiblyConflictingResourceDiff({
					baseResourceId: resourceCommit.thing_id,
					headResourceId: response.data.headCommit.commit.thing_id,
					courseId
				})
			);
			store.dispatch(updateCourseConfigAndHeadCommit({ ...response.data, courseId }));

			const [data, error] = await resolvePromise(fetchResourceDiffPromise);
			const state = store.getState();

			if (data && !state.courses[courseId].conflicts?.length) {
				// no conflicts found - save again

				const updatedPayload = {
					payload: createChangesPayload(state.courses[courseId]),
					resourceCommit: state.courses[courseId].headCommit, // updated headCommit
					courseId
				};

				try {
					validateYAMLConfig(state.courses[courseId].book.yaml_config);
					validateChangesPayload(updatedPayload.payload);

					store.dispatch(saveResourceChanges(updatedPayload));
				} catch (error) {
					showErrorToast(error.message);
				}
			} else if (data && state.courses[courseId].conflicts.length) {
				// conflicts found - fetch changes commits with creators names

				store.dispatch(fetchCommits({ courseId, baseCommitId: resourceCommit.id }));
				store.dispatch(setShowConflictsModal({ courseId, showModal: true }));

				showErrorToast(
					'Resource update rejected: please resolve the conflicts to have the latest version.'
				);
			} else if (error) {
				console.error(error.message);
				throw Error(error.message);
			}
		}
	}
);

// Conflict-aware dispatch shared by the websocket and visibility paths. The
// caller is responsible for fetching the current resource head and passing it
// in; this helper only decides how to apply it.
export const handleResourceUpdateNotification = (courseId: number, remoteHeadData: any) => {
	// A notification can arrive before the course slice is populated (or after
	// cleanup), so bail out if it isn't loaded.
	const course = store.getState().courses[courseId];
	if (!course) return;

	// If there are unresolved conflicts, keep the incoming diff pending and apply
	// it once the current conflicts are resolved; otherwise apply the update now.
	if (course.conflicts?.length) {
		store.dispatch(fetchPendingDiff({ courseId, remoteHeadData }));
	} else {
		store.dispatch(updateResourceOnPushNotification({ courseId, remoteHeadData }));
	}
};

// Websocket push notifications are only delivered while a tab is connected and
// scoped to the page the client is viewing, so an update made elsewhere (or
// while the tab was hidden/backgrounded) can be missed. Whenever the page
// becomes visible again we re-check that our resource is current and, if it
// isn't, handle it exactly as if a websocket notification had told us.
export const checkResourceSyncOnVisible = createAsyncThunk(
	'checkResourceSyncOnVisible',
	async (payload: { courseId: number }) => {
		const { courseId } = payload;
		const course = store.getState().courses[courseId];

		// the course may not be loaded yet (e.g. on initial mount)
		if (!course?.headCommit) return;

		const response = await getResourceCurrentHead(courseId);
		const remoteHeadData = response.data;
		const remoteResourceId = remoteHeadData.headCommit.commit.thing_id;

		if (remoteResourceId !== course.headCommit.thing_id) {
			handleResourceUpdateNotification(courseId, remoteHeadData);
		}
	}
);

export const updateResourceOnPushNotification = createAsyncThunk(
	'updateResourceOnPushNotification',
	async (payload: { courseId: number; remoteHeadData: any }) => {
		// The caller is responsible for fetching the current resource head; this
		// thunk only applies it.
		const { courseId, remoteHeadData } = payload;

		// not fast-forward update

		const resourceCommit = store.getState().courses[courseId].headCommit;

		const fetchResourceDiffPromise = store.dispatch(
			fetchResourceDiffOnPushNotification({
				baseResourceId: resourceCommit.thing_id,
				headResourceId: remoteHeadData.headCommit.commit.thing_id,
				courseId
			})
		);
		store.dispatch(updateCourseConfigAndHeadCommit({ ...remoteHeadData, courseId }));

		const [data, error] = await resolvePromise(fetchResourceDiffPromise);

		if (data && store.getState().courses[courseId].conflicts?.length) {
			// conflicts found - fetch changes commits with creators names
			store.dispatch(fetchCommits({ courseId, baseCommitId: resourceCommit.id }));
			showErrorToast('Please resolve the conflicts to use the latest course version.');
		} else if (error) {
			console.error(error.message);
		}
	}
);

export const fetchPendingDiff = createAsyncThunk(
	'fetchPendingDiff',
	async (payload: { courseId: number; remoteHeadData: any }) => {
		// don't apply the diff, keep the diff as pending. The caller is responsible
		// for fetching the current resource head; this thunk only fetches the diff.
		const { courseId, remoteHeadData } = payload;

		const resourceCommit = store.getState().courses[courseId].headCommit;
		const diffPayload = {
			baseResourceId: resourceCommit.thing_id,
			headResourceId: remoteHeadData.headCommit.commit.thing_id,
			courseId
		};

		const diffResponse = await retrieveSubtreeDiff(diffPayload);
		const commitsResponse = await getCommits({ courseId, baseCommitId: resourceCommit.id });

		return {
			nodes: diffResponse.data.nodes,
			headCommit: remoteHeadData.headCommit.commit.thing_id,
			commits: commitsResponse.data.commits
		};
	}
);

export const updatePageNumbers = createAsyncThunk('updatePageNumbers', async (payload: any) => {
	const { courseId, resourceId } = payload;

	repaginate(courseId).then(({ data }) => {
		const { thing_id: headResourceId } = data.resourceCommit.commit;

		store.dispatch(
			fetchPossiblyConflictingResourceDiff({
				baseResourceId: resourceId,
				headResourceId,
				courseId
			})
		);
		store.dispatch(
			showToast({
				severity: ToastSeverity.Success,
				message: 'Page numbers successfully updated'
			})
		);
	});
});

export const generateNewGlossaryPage = createAsyncThunk(
	'generateNewGlossaryPage',
	async (payload: any) => {
		const { courseId, resourceId } = payload;

		generateGlossary(courseId).then(({ data }) => {
			const { thing_id: headResourceId } = data.resourceCommit.commit;

			store.dispatch(
				fetchPossiblyConflictingResourceDiff({
					baseResourceId: resourceId,
					headResourceId,
					courseId
				})
			);
			store.dispatch(
				showToast({
					severity: ToastSeverity.Success,
					message: 'Glossary page successfully generated'
				})
			);
		});
	}
);

export const generateMCQPQuestions = createAsyncThunk(
	'generateMCQPQuestions',
	async (context: QuestionGenerationContext) => {
		store.dispatch(
			storeQuestionGenerationPayload({ ...context, questionGenerationPayload: context.payload })
		);
		const generateQuestionsPromise = getGeneratedQuestions(context);

		const [response, error] = await resolvePromise(generateQuestionsPromise);

		if (error) {
			const message = error.response?.data?.error?.message || error.message;
			showErrorToast(message);
			console.error(message);
			throw Error(error.message);
		} else {
			try {
				// Parse OpenAI JSON response and map it to MCQP question elements
				const content = response.data;
				store.dispatch(
					storeQuestionGenerationResponse({ ...context, questionGenerationResponse: content })
				);
				const questions = content.questions.map((question) => {
					const newQuestion = getNewElement('MCQuestion') as MCQuestion;

					const choices = question.choices.map((choice) => {
						const { body, rejoinder, is_correct } = choice;
						const newChoice = getNewElement('MCChoice') as MCChoicePayload;
						return { ...newChoice, body, rejoinder, is_correct };
					});

					return { ...newQuestion, body: question.body, choices };
				});

				store.dispatch(
					showToast({
						severity: ToastSeverity.Success,
						message: 'Questions are successfully generated.'
					})
				);

				return { questions };
			} catch (error) {
				showErrorToast("Couldn't generate questions: Error parsing a JSON response.");
				console.error(error.message);
				throw Error(error.message);
			}
		}
	}
);

// Generates key concepts for an EOC check-in chatbot element from the chapter's
// content (server-side, via the generate_concepts Lambda action). The results
// are appended to the element's existing key_concepts so the author can adjust
// them before saving. A transient `isGeneratingConcepts` flag drives UI state.
export const generateEocCheckinConcepts = createAsyncThunk(
	'generateEocCheckinConcepts',
	async (context: ConceptGenerationContext) => {
		const { courseId, elementId } = context;
		store.dispatch(setEocConceptsGenerating({ courseId, elementId, isGenerating: true }));

		const [response, error] = await resolvePromise(getGeneratedConcepts(context));

		if (error) {
			store.dispatch(setEocConceptsGenerating({ courseId, elementId, isGenerating: false }));
			const message = error.response?.data?.error?.message || error.message;
			showErrorToast(message);
			console.error(message);
			throw Error(error.message);
		}

		const rawConcepts = response.data?.key_concepts ?? [];
		const concepts = rawConcepts.map((concept) => ({
			concept: concept.concept ?? '',
			description: concept.description ?? ''
		}));

		if (concepts.length) {
			store.dispatch(appendEocKeyConcepts({ courseId, elementId, concepts }));
		}
		store.dispatch(setEocConceptsGenerating({ courseId, elementId, isGenerating: false }));

		store.dispatch(
			showToast({
				severity: concepts.length ? ToastSeverity.Success : ToastSeverity.Warning,
				message: concepts.length
					? `Added ${concepts.length} generated key concept${
							concepts.length === 1 ? '' : 's'
						}. Review and adjust them as needed.`
					: 'No key concepts were generated. Try adding more chapter content first.'
			})
		);

		return { concepts };
	}
);
